import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Divider,
  Tooltip,
  LinearProgress,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import pathfinderApi, { EntityNote, NoteAttachment } from '../../services/pathfinderApi';

interface NotesDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  watchlistId?: number;
  geofenceId?: number;
}

const NotesDialog: React.FC<NotesDialogProps> = ({
  open,
  onClose,
  title,
  watchlistId,
  geofenceId
}) => {
  const [notes, setNotes] = useState<EntityNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Attachment state
  const [attachments, setAttachments] = useState<Map<number, NoteAttachment[]>>(new Map());
  const [uploadingNoteId, setUploadingNoteId] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [processingAttachmentId, setProcessingAttachmentId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentUploadNoteId = useRef<number | null>(null);

  const loadNotes = useCallback(async () => {
    if (!watchlistId && !geofenceId) return;
    
    setLoading(true);
    try {
      const data = await pathfinderApi.getNotes({ 
        watchlist_id: watchlistId, 
        geofence_id: geofenceId 
      });
      setNotes(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [watchlistId, geofenceId]);

  useEffect(() => {
    if (open) {
      loadNotes();
    }
  }, [open, loadNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setSubmitting(true);
    try {
      const note = await pathfinderApi.createNote(newNote, watchlistId, geofenceId);
      setNotes([note, ...notes]);
      setNewNote('');
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await pathfinderApi.deleteNote(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (error) {
    }
  };

  // Load attachments for a note
  const loadAttachments = useCallback(async (noteId: number) => {
    if (attachments.has(noteId)) return;
    
    try {
      const noteAttachments = await pathfinderApi.getNoteAttachments(noteId);
      setAttachments(prev => new Map(prev).set(noteId, noteAttachments));
    } catch (error) {
      console.error('Failed to load attachments:', error);
    }
  }, [attachments]);

  // Load attachments for all notes when dialog opens
  useEffect(() => {
    if (open && notes.length > 0) {
      notes.forEach(note => loadAttachments(note.id));
    }
  }, [open, notes, loadAttachments]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const noteId = currentUploadNoteId.current;
    
    if (!file || !noteId) return;
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    setUploadingNoteId(noteId);
    setUploadProgress(0);
    
    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);
      
      const attachment = await pathfinderApi.uploadNoteAttachment(noteId, file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setAttachments(prev => {
        const noteAttachments = prev.get(noteId) || [];
        return new Map(prev).set(noteId, [...noteAttachments, attachment]);
      });
      
      setTimeout(() => {
        setUploadingNoteId(null);
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      setUploadingNoteId(null);
      setUploadProgress(0);
    }
  }, []);

  // Trigger file upload
  const triggerUpload = useCallback((noteId: number) => {
    currentUploadNoteId.current = noteId;
    fileInputRef.current?.click();
  }, []);

  // Delete attachment
  const handleDeleteAttachment = useCallback(async (attachmentId: number, noteId: number) => {
    try {
      await pathfinderApi.deleteNoteAttachment(attachmentId);
      setAttachments(prev => {
        const noteAttachments = prev.get(noteId) || [];
        return new Map(prev).set(noteId, noteAttachments.filter(a => a.id !== attachmentId));
      });
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    }
  }, []);

  // Process attachment and append insights to note
  const handleProcessAttachment = useCallback(async (attachmentId: number, _noteId: number) => {
    setProcessingAttachmentId(attachmentId);
    
    try {
      const result = await pathfinderApi.appendInsightsToNote(attachmentId);
      
      if (result.success) {
        // Reload the notes to show updated content
        await loadNotes();
        alert('Document insights extracted and added to note!');
      }
    } catch (error: any) {
      console.error('Failed to process attachment:', error);
      alert(error.response?.data?.detail || 'Failed to process document. Make sure Azure OpenAI is configured.');
    } finally {
      setProcessingAttachmentId(null);
    }
  }, [loadNotes]);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, mt: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Add a new note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            disabled={submitting}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={submitting ? <CircularProgress size={16} /> : <NoteAddIcon />}
              onClick={handleAddNote}
              disabled={!newNote.trim() || submitting}
            >
              Add Note
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">HISTORY</Typography>
        </Divider>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No notes yet. Add one above to start a log.
          </Typography>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {notes.map((note) => (
              <ListItem
                key={note.id}
                alignItems="flex-start"
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Attach file">
                      <IconButton 
                        edge="end" 
                        size="small" 
                        onClick={() => triggerUpload(note.id)}
                        disabled={uploadingNoteId === note.id}
                      >
                        {uploadingNoteId === note.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <AttachFileIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete note">
                      <IconButton edge="end" size="small" onClick={() => handleDeleteNote(note.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                sx={{ 
                  bgcolor: 'background.paper', 
                  mb: 1, 
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', pr: 8 }}>
                      {note.content}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {note.created_by}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(note.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                />
                
                {/* Upload progress */}
                {uploadingNoteId === note.id && (
                  <Box sx={{ mt: 1, px: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                )}
                
                {/* Attachments */}
                {attachments.get(note.id)?.length ? (
                  <Box sx={{ 
                    mt: 1, 
                    pt: 1, 
                    borderTop: 1, 
                    borderColor: 'divider',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}>
                    {attachments.get(note.id)?.map(attachment => (
                      <Box
                        key={attachment.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          p: 0.5,
                          pl: 1,
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          maxWidth: 180,
                        }}
                      >
                        {attachment.is_image ? (
                          <Box
                            component="img"
                            src={pathfinderApi.getNoteAttachmentDownloadUrl(attachment.id)}
                            alt={attachment.original_filename}
                            onClick={() => setPreviewImage(pathfinderApi.getNoteAttachmentDownloadUrl(attachment.id))}
                            sx={{
                              width: 28,
                              height: 28,
                              objectFit: 'cover',
                              borderRadius: 0.5,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 },
                            }}
                          />
                        ) : (
                          <InsertDriveFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        )}
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={attachment.original_filename}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 80,
                              }}
                            >
                              {attachment.original_filename}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {formatFileSize(attachment.file_size)}
                          </Typography>
                        </Box>

                        <Tooltip title="Extract insights with AI">
                          <IconButton
                            size="small"
                            onClick={() => handleProcessAttachment(attachment.id, note.id)}
                            disabled={processingAttachmentId === attachment.id}
                            sx={{ 
                              p: 0.25, 
                              color: 'primary.main',
                              '&:hover': { color: 'primary.dark' } 
                            }}
                          >
                            {processingAttachmentId === attachment.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <AutoFixHighIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            component="a"
                            href={pathfinderApi.getNoteAttachmentDownloadUrl(attachment.id)}
                            target="_blank"
                            sx={{ p: 0.25 }}
                          >
                            <DownloadIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAttachment(attachment.id, note.id)}
                            sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </ListItem>
            ))}
          </List>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.xml,.kml,.kmz,.gpx"
        />
      </DialogContent>

      {/* Image preview dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default NotesDialog;

